import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  IconButton,
  Image,
  Portal,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload
} from 'react-icons/fa';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from '../api';
import PageLayout from './PageLayout';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const THUMBNAIL_BATCH = 10;

const clampScale = (value) => Math.min(4, Math.max(1, value));

const getDistance = (touches) => {
  if (touches.length < 2) return 0;
  const [a, b] = touches;
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
};

const getCenter = (touches) => {
  if (touches.length < 2) return { x: 0, y: 0 };
  const [a, b] = touches;
  return {
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2
  };
};

const GaleriaDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const initialGallery = location.state?.gallery ?? null;
  const [gallery, setGallery] = useState(initialGallery);
  const [photos, setPhotos] = useState([]);
  const [visibleCount, setVisibleCount] = useState(THUMBNAIL_BATCH);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const zoomContainerRef = useRef(null);
  const zoomStateRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 });
  const lastScaleRef = useRef(1);
  const initialDistanceRef = useRef(null);
  const initialCenterRef = useRef({ x: 0, y: 0 });
  const startOffsetRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const [zoomState, setZoomState] = useState({ scale: 1, offsetX: 0, offsetY: 0 });

  const updateZoomState = useCallback((updater) => {
    setZoomState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      zoomStateRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    zoomStateRef.current = zoomState;
  }, [zoomState]);

  useEffect(() => {
    if (gallery && String(gallery.id_galeria) === String(id)) {
      setLoading(false);
      return;
    }

    const loadGallery = async () => {
      try {
        const response = await axios.get(`${API_BASE}/getGaleriaInstiuticao?id_insituicao=1`);
        const galeriasData = response.data.result?.[0] || [];
        const match = galeriasData.find((item) => String(item.id_galeria) === String(id));
        if (!match) {
          setError('Galeria não encontrada.');
          toast({
            title: 'Erro',
            description: 'Galeria não encontrada.',
            status: 'error',
            duration: 4000,
            isClosable: true
          });
          return;
        }
        setGallery(match);
      } catch (err) {
        console.error('Erro ao carregar galeria:', err);
        setError('Não foi possível carregar a galeria.');
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar a galeria.',
          status: 'error',
          duration: 4000,
          isClosable: true
        });
      } finally {
        setLoading(false);
      }
    };

    loadGallery();
  }, [gallery, id, toast]);

  useEffect(() => {
    if (!gallery) return;
    const fetchFotos = async () => {
      try {
        const response = await fetch(`${API_BASE}/getFotosGaleria?id_galeria=${gallery.id_galeria}`);
        if (response.ok) {
          const data = await response.json();
          setPhotos(data.fotos || []);
          setPhotoIndex(0);
          setVisibleCount(THUMBNAIL_BATCH);
        }
      } catch (err) {
        console.error('Erro ao buscar fotos da galeria:', err);
      }
    };

    setPhotos([]);
    fetchFotos();
  }, [gallery]);

  const handleOpenViewer = (index) => {
    setPhotoIndex(index);
    setViewerOpen(true);
  };

  const goPrev = () => {
    if (!photos.length) return;
    setPhotoIndex((prev) => (prev + photos.length - 1) % photos.length);
  };

  const goNext = () => {
    if (!photos.length) return;
    setPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const handleLoadMore = () => {
    if (!photos.length) return;
    setVisibleCount((prev) => Math.min(prev + THUMBNAIL_BATCH, photos.length));
  };

  const handleDownload = () => {
    if (!photos.length) return;
    const current = photos[photoIndex];
    if (!current || !gallery) return;
    const downloadUrl = `${API_BASE}/Galerias/${gallery.id_galeria}/${current}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = current;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetZoom = useCallback(() => {
    lastScaleRef.current = 1;
    initialDistanceRef.current = null;
    initialCenterRef.current = { x: 0, y: 0 };
    startOffsetRef.current = { x: 0, y: 0 };
    panStartRef.current = { x: 0, y: 0 };
    updateZoomState({ scale: 1, offsetX: 0, offsetY: 0 });
  }, [updateZoomState]);

  const handleCloseViewer = useCallback(() => {
    setViewerOpen(false);
    resetZoom();
  }, [resetZoom]);

  const handleTouchStart = useCallback(
    (event) => {
      if (event.touches.length === 2) {
        initialDistanceRef.current = getDistance(event.touches);
        initialCenterRef.current = getCenter(event.touches);
        startOffsetRef.current = {
          x: zoomStateRef.current.offsetX,
          y: zoomStateRef.current.offsetY
        };
        panStartRef.current = { x: initialCenterRef.current.x, y: initialCenterRef.current.y };
      } else if (event.touches.length === 1 && zoomStateRef.current.scale > 1) {
        const touch = event.touches[0];
        panStartRef.current = { x: touch.clientX, y: touch.clientY };
        startOffsetRef.current = {
          x: zoomStateRef.current.offsetX,
          y: zoomStateRef.current.offsetY
        };
      }
    },
    []
  );

  const handleTouchMove = useCallback(
    (event) => {
      if (event.touches.length === 2 && initialDistanceRef.current) {
        event.preventDefault();
        const distance = getDistance(event.touches);
        const center = getCenter(event.touches);
        const scaleFactor = distance / initialDistanceRef.current;
        const nextScale = clampScale(lastScaleRef.current * scaleFactor);
        const dx = center.x - initialCenterRef.current.x;
        const dy = center.y - initialCenterRef.current.y;
        updateZoomState({
          scale: nextScale,
          offsetX: startOffsetRef.current.x + dx,
          offsetY: startOffsetRef.current.y + dy
        });
      } else if (event.touches.length === 1 && zoomStateRef.current.scale > 1) {
        event.preventDefault();
        const touch = event.touches[0];
        const dx = touch.clientX - panStartRef.current.x;
        const dy = touch.clientY - panStartRef.current.y;
        updateZoomState((prev) => ({
          scale: prev.scale,
          offsetX: startOffsetRef.current.x + dx,
          offsetY: startOffsetRef.current.y + dy
        }));
      }
    },
    [updateZoomState]
  );

  const handleTouchEnd = useCallback(() => {
    lastScaleRef.current = zoomStateRef.current.scale;
    if (zoomStateRef.current.scale === 1) {
      updateZoomState({ scale: 1, offsetX: 0, offsetY: 0 });
      startOffsetRef.current = { x: 0, y: 0 };
    } else {
      startOffsetRef.current = {
        x: zoomStateRef.current.offsetX,
        y: zoomStateRef.current.offsetY
      };
    }
  }, [updateZoomState]);

  const handleWheel = useCallback(
    (event) => {
      event.preventDefault();
      const deltaScale = -event.deltaY * 0.002;
      updateZoomState((prev) => {
        const nextScale = clampScale(prev.scale + deltaScale);
        lastScaleRef.current = nextScale;
        return { ...prev, scale: nextScale };
      });
    },
    [updateZoomState]
  );

  useEffect(() => {
    if (viewerOpen) {
      resetZoom();
    }
  }, [photoIndex, viewerOpen, resetZoom]);

  useEffect(() => {
    const container = zoomContainerRef.current;
    if (!viewerOpen || !container) return undefined;
    const opts = { passive: false };
    container.addEventListener('touchstart', handleTouchStart, opts);
    container.addEventListener('touchmove', handleTouchMove, opts);
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('touchstart', handleTouchStart, opts);
      container.removeEventListener('touchmove', handleTouchMove, opts);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [viewerOpen, handleTouchStart, handleTouchMove, handleTouchEnd, handleWheel]);

  useEffect(() => {
    if (!viewerOpen) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [viewerOpen]);

  const visiblePhotos = photos.slice(0, visibleCount);
  const hasMorePhotos = visibleCount < photos.length;

  const heroImage = gallery?.cover || (photos[0] ? `${API_BASE}/Galerias/${gallery?.id_galeria}/${photos[0]}` : null);

  const zoomTransformStyle = {
    transform: `scale(${zoomState.scale}) translate(${zoomState.offsetX}px, ${zoomState.offsetY}px)`,
    transformOrigin: 'center center',
    transition: zoomState.scale === 1 ? 'transform 0.3s ease' : 'none'
  };

  return (
    <PageLayout
      title="Memórias Fotográficas"
      subtitle={gallery?.titulo ? 'Galeria' : 'Galerias'}
      onBack={() => navigate('/memorias-fotograficas')}
      accent="#c9a24c"
      background="#f8fafc"
      watermark="/maconaria.png"
    >
      {loading && (
        <Flex align="center" justify="center" py={20}>
          <Spinner size="lg" color="blue.500" />
        </Flex>
      )}

      {!loading && error && (
        <Box
          bg="white"
          borderRadius="22px"
          p={6}
          boxShadow="0 12px 30px rgba(15,23,42,0.12)"
        >
          <Text color="red.600" textAlign="center">{error}</Text>
        </Box>
      )}

      {!loading && !error && gallery && (
        <Stack spacing={6}>
          <Box bg="white" borderRadius="28px" boxShadow="0 20px 60px rgba(15,23,42,0.12)" overflow="hidden">
            {heroImage && (
              <Image
                src={heroImage}
                alt={gallery.titulo}
                objectFit="cover"
                width="100%"
                height={{ base: '220px', md: '320px' }}
              />
            )}
            <Box p={{ base: 5, md: 8 }}>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">{gallery.titulo}</Text>
              <HStack spacing={3} mt={3}>
                <Badge colorScheme="blue" variant="subtle">{formatDate(gallery.data)}</Badge>
                {gallery.assento && (
                  <Badge colorScheme="gray" variant="subtle">Assento: {gallery.assento}</Badge>
                )}
              </HStack>
            </Box>
          </Box>

          {gallery.descricao && (
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: 4, md: 6 }}
              boxShadow="0 12px 30px rgba(15,23,42,0.08)"
            >
              <Text
                fontSize="md"
                color="gray.700"
                lineHeight="tall"
                dangerouslySetInnerHTML={{ __html: gallery.descricao }}
              />
            </Box>
          )}

          <Box
            bg="white"
            borderRadius="28px"
            p={{ base: 4, md: 6 }}
            boxShadow="0 20px 60px rgba(15,23,42,0.1)"
          >
            <Flex align="center" justify="space-between" mb={5}>
              <HStack spacing={3}>
                <Text fontSize="lg" fontWeight="bold" color="gray.800">Fotos</Text>
                <Badge colorScheme="blue" variant="subtle">{photos.length} fotos</Badge>
              </HStack>
              <Button size="sm" leftIcon={<FaDownload />} colorScheme="blue" onClick={handleDownload} isDisabled={!photos.length}>
                Baixar
              </Button>
            </Flex>

          {photos.length === 0 ? (
            <Box bg="gray.50" borderRadius="20px" p={8} textAlign="center">
              <Text color="gray.500">Nenhuma foto disponível ainda.</Text>
            </Box>
          ) : (
            <>
              <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={4}>
                {visiblePhotos.map((photo, index) => (
                  <Box
                    key={photo}
                    cursor="pointer"
                    borderRadius="20px"
                    overflow="hidden"
                    bg="gray.50"
                    boxShadow="0 10px 30px rgba(15,23,42,0.08)"
                    onClick={() => handleOpenViewer(index)}
                  >
                    <Image
                      src={`${API_BASE}/Galerias/${gallery.id_galeria}/${photo}`}
                      alt={`Foto ${index + 1}`}
                      objectFit="cover"
                      width="100%"
                      height="180px"
                    />
                    <Box p={3}>
                      <Text fontSize="sm" color="gray.500">Foto {index + 1}</Text>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
              {hasMorePhotos && (
                <Flex justify="center" mt={4}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    onClick={handleLoadMore}
                  >
                    Carregar mais fotos
                  </Button>
                </Flex>
              )}
            </>
          )}
          </Box>
        </Stack>
      )}

      {viewerOpen && (
        <Portal>
          <Box
            position="fixed"
            inset="0"
            bg="rgba(4,7,15,0.92)"
            zIndex={1400}
            display="flex"
            flexDirection="column"
            color="white"
          >
            <Flex
              align="center"
              justify="space-between"
              px={{ base: 4, md: 10 }}
              py={4}
            >
              <Box>
                <Text fontSize="sm" color="whiteAlpha.700">
                  Galeria
                </Text>
                <Text fontSize="xl" fontWeight="bold">
                  {gallery?.titulo}
                </Text>
              </Box>
              <HStack spacing={3}>
                <Badge colorScheme="blue" variant="subtle">
                  {photoIndex + 1} / {photos.length}
                </Badge>
                <Button
                  leftIcon={<FaDownload />}
                  onClick={handleDownload}
                  colorScheme="blue"
                  variant="ghost"
                >
                  Download
                </Button>
                <CloseButton
                  color="white"
                  border="1px solid rgba(255,255,255,0.3)"
                  onClick={handleCloseViewer}
                  size="lg"
                />
              </HStack>
            </Flex>

            <Flex
              flex="1"
              px={{ base: 3, md: 10 }}
              align="center"
              justify="center"
              gap={4}
            >
              <IconButton
                aria-label="Foto anterior"
                icon={<FaChevronLeft />}
                onClick={goPrev}
                isDisabled={photos.length <= 1}
                colorScheme="blue"
                variant="ghost"
                size="lg"
              />

              <Box flex="1" textAlign="center" maxW="100%">
                <Box
                  ref={zoomContainerRef}
                  display="inline-flex"
                  width="100%"
                  maxH="70vh"
                  justifyContent="center"
                  cursor={zoomState.scale > 1 ? 'grabbing' : 'grab'}
                  style={{ touchAction: zoomState.scale > 1 ? 'none' : 'auto' }}
                >
                  {photos[photoIndex] && (
                    <Image
                      src={`${API_BASE}/Galerias/${gallery?.id_galeria}/${photos[photoIndex]}`}
                      alt="Foto selecionada"
                      maxH="70vh"
                      width="100%"
                      borderRadius="18px"
                      objectFit="contain"
                      boxShadow="0 20px 50px rgba(0,0,0,0.4)"
                      style={zoomTransformStyle}
                      onLoad={() => resetZoom()}
                    />
                  )}
                </Box>
              </Box>

              <IconButton
                aria-label="Próxima foto"
                icon={<FaChevronRight />}
                onClick={goNext}
                isDisabled={photos.length <= 1}
                colorScheme="blue"
                variant="ghost"
                size="lg"
              />
            </Flex>

            <Box
              px={{ base: 2, md: 6 }}
              py={4}
              overflowX="auto"
              bg="rgba(15,23,42,0.65)"
            >
              <Flex gap={3}>
                {photos.map((photo, index) => (
                  <Box
                    key={photo}
                    borderRadius="12px"
                    overflow="hidden"
                    cursor="pointer"
                    border={index === photoIndex ? '2px solid #63B3ED' : '2px solid transparent'}
                    onClick={() => setPhotoIndex(index)}
                  >
                    <Image
                      src={`${API_BASE}/Galerias/${gallery?.id_galeria}/${photo}`}
                      alt={`Thumb ${index + 1}`}
                      boxSize={{ base: '70px', md: '85px' }}
                      objectFit="cover"
                      transition="transform 0.2s"
                      _hover={{ transform: 'scale(1.05)' }}
                    />
                  </Box>
                ))}
              </Flex>
            </Box>
          </Box>
        </Portal>
      )}
    </PageLayout>
  );
};

export default GaleriaDetailPage;
